/* El guion del escritor en el panel: los tipos y la poca lógica que es del panel (sin Word ni red). */

export type EscenaIndice = { numero: string; hilo: string | null; pieza: string; palabras: number | null; titulo: string };
export type CapituloIndice = { n: number | null; titulo: string; acto: string | null; linea: string; escenas: EscenaIndice[] };
/** La biblia de este libro según el motor: cuál se usa, de cuándo es y qué otras versiones hay; o que no la hay (o que hay de varios libros). */
export type Biblia = { hay: true; nombre: string; cuando: string | null; otras: string[] } | { hay: false; varias?: string[] };
export type Indice = {
  hay: true; titulo: string; fuente: string | null; importado: string | null; avisos: string[]; secciones: string[];
  /** Un motor anterior no lo manda: entonces no se dice nada de la biblia. */
  biblia?: Biblia;
  resumen: { capitulos: number; escenas: number; palabras: number; campos: string[] };
  capitulos: CapituloIndice[];
  desfasado: boolean; cambiado: boolean; falta: boolean; mas_reciente: string | null;
  /** Si el documento abierto ya no es la última versión del manuscrito, cuál lo es. */
  version_mas_nueva?: string | null;
  /** El manuscrito del libro en su carpeta; null si todavía no tiene (un motor anterior no lo manda: undefined). */
  manuscrito?: string | null;
  /** Con la novela construida abierta (en `HumanInk/`): la carpeta del libro y el nombre de la novela (21-oct). */
  libro?: string;
  novela_abierta?: string;
  /** Los otros guiones de la carpeta, para traer otro en lugar del que hay. */
  otros_guiones?: string[];
  /** La escena en la que estaba el autor en este manuscrito (en esta versión o en la anterior). */
  escena_reciente?: string | null;
  /** Las escenas cerradas de la novela, y cuánto llevan entre todas. */
  novela?: NovelaEstado;
  autoria?: Autoria;
};
/**
 * El registro de autoría de la carpeta del libro: si está empezado, desde cuándo y con qué punto de
 * entrada. `de_este_guion`: se empezó desde este guion (y no es el de otro libro que comparte carpeta).
 */
export type Autoria = {
  empezado: boolean; ilegible?: boolean; entry_point?: string; entry_ts?: string; coverage?: number; titulo?: string;
  de_este_guion?: boolean; libros_en_la_carpeta?: number;
};
/** Con qué llega el autor al empezar el registro. De más a menos hecho. */
export const ENTRADAS: [string, string][] = [
  ["escaleta", "Con la biblia y el guion hechos"],
  ["biblia", "Con la biblia; el guion lo hago ahora"],
  ["premisa", "Solo con la idea"],
];
export const nombreDeEntrada = (p: string | undefined) => ENTRADAS.find(([id]) => id === p)?.[1].toLowerCase() ?? (p || "sin decir");
export type SinGuion = { hay: false; candidatos: string[]; biblia?: Biblia };
export type Ficha = EscenaIndice & {
  cabecera: string; campos: [string, string][];
  capitulo: { n: number | null; titulo: string; acto: string | null; linea: string; escenas: number; orden: number };
  anterior: string | null; siguiente: string | null;
};
/** Una muletilla de las que más se repiten: la frase tal cual, o qué es si es una estructura larga. */
export type Muletilla = { frase: string | null; que: string; veces: number };
/**
 * Las muletillas de IA de un texto (25-oct-2026; antes, la huella de 0 a 100): una cuenta, no un veredicto.
 * `por_mil` solo se dice con 500 palabras o más (`poca_muestra` falso).
 */
export type Muletillas = { hay: false; palabras: number } | { hay: true; n: number; palabras: number; por_mil: number; mas_repetidas: Muletilla[]; poca_muestra: boolean };
/**
 * Lo que el panel enseña SIEMPRE de la escena elegida: sus palabras y sus muletillas, leídas del documento
 * guardado, y las de la novela entera (todo lo escrito, de una vez).
 */
export type MedidaDeEscena = { escena: string; leido: boolean; guardado: string | null; en_el_documento: boolean; palabras: number; previstas: number | null;
  /** El documento abierto es la biblia, el guion…: no es el manuscrito (21-oct). */
  de_partida?: boolean;
  muletillas: Muletillas; novela: Muletillas & { escenas: number } };

/** «“en este sentido” (2 veces), una estructura paralela binaria». */
export const nombresDeMuletillas = (lista: Muletilla[] | null | undefined): string =>
  (lista ?? []).map((m) => `${m.frase ? `«${m.frase}»` : m.que}${m.veces > 1 ? ` (${m.veces} veces)` : ""}`).join(", ");

/** «ninguna», «1», «14 · 1,2 por cada 1.000 palabras»; sin texto, lo dice. */
export function fraseDeMuletillas(m: Muletillas | null | undefined): string {
  if (!m) return "";
  if (!m.hay) return "sin texto";
  if (!m.n) return "ninguna";
  return m.poca_muestra ? String(m.n) : `${m.n} · ${String(m.por_mil).replace(".", ",")} por cada 1.000 palabras`;
}

/** De qué escena del manuscrito GUARDADO es lo seleccionado, y cuántas palabras tiene esa escena entera. */
export type Situada = { numero: string | null; titulo?: string; palabras?: number; guardado?: string | null };

/** Lo que dice la verificación de un campo de la ficha. `sin_prueba`: dice que cumple y su cita no está en la escena. */
/** Una de las cosas concretas que pide un campo de la ficha, y qué ha pasado con ella. */
export type Comprobacion = { n: number; campo: string; pide: string; clase: "hecho" | "literal" | "ausencia" | "juicio";
  estado: "si" | "no" | "sin_prueba" | "sin_respuesta" | "acordado" | "opinion_si" | "opinion_no";
  por?: "programa" | "modelo" | "anterior" | "coach"; cita: string | null; porque: string };
export type CampoVerificado = { campo: string; veredicto: "cumple" | "a medias" | "falta" | null; cita: string | null; porque: string; sin_prueba: boolean;
  /** `opinion`: el campo solo pide cosas que no se pueden probar con una cita; lo que se dice es una opinión. */
  opinion?: boolean; cuenta?: { si: number; total: number; opiniones: number; acordadas: number }; comprobaciones?: Comprobacion[] };
export type CuentaDeCampos = { cumple: number; a_medias: number; falta: number; opinion: number; sin_veredicto: number; total: number };

/** «6 de 8 campos cumplen · 1 a medias · 1 es una opinión»: la cuenta la hace el programa. */
export function fraseDeCampos(r: CuentaDeCampos | null | undefined): string {
  if (!r || !r.total) return "";
  const contados = r.total - r.opinion;
  const dice = (n: number, uno: string, varios: string) => (n > 0 ? `${n} ${n === 1 ? uno : varios}` : "");
  const cabeza = contados === 0 ? "" : r.cumple === contados ? (contados === 1 ? "El campo cumple" : `Los ${contados} campos cumplen`) : `${r.cumple} de ${contados} campos cumplen`;
  return [cabeza, dice(r.a_medias, "a medias", "a medias"), dice(r.falta, "falta", "faltan"), dice(r.sin_veredicto, "sin comprobar", "sin comprobar"),
    dice(r.opinion, "es una opinión", "son opiniones")].filter(Boolean).join(" · ") + ".";
}

/**
 * Lo que se dice de un campo con lista, tras su veredicto: la cuenta y lo acordado. Lo que falta no se
 * repite aquí: va debajo, comprobación a comprobación.
 */
export function cuentaDeCampo(v: Pick<CampoVerificado, "cuenta">): string {
  const c = v.cuenta;
  if (!c) return "";
  const cuenta = c.total > 1 ? `${c.si} de ${c.total} comprobaciones.` : "";
  const acordado = c.acordadas === 1 ? "Una ya no se exige: lo acordaste con el coach." : c.acordadas > 1 ? `${c.acordadas} ya no se exigen: lo acordaste con el coach.` : "";
  return [cuenta, acordado].filter(Boolean).join(" ");
}

/** La marca de una comprobación y lo que quiere decir, para quien no ha visto nunca la lista. */
export function marcaDe(estado: Comprobacion["estado"]): { marca: string; dice: string } {
  switch (estado) {
    case "si": return { marca: "✓", dice: "se cumple" };
    case "no": return { marca: "✗", dice: "no se cumple" };
    case "sin_prueba": return { marca: "?", dice: "sin una cita que lo pruebe" };
    case "sin_respuesta": return { marca: "–", dice: "sin comprobar" };
    case "acordado": return { marca: "○", dice: "ya no se exige: lo acordaste con el coach" };
    case "opinion_si": return { marca: "✓", dice: "opinión: bien" };
    default: return { marca: "✗", dice: "opinión" };
  }
}

/** Las comprobaciones que piden atención: las que no se cumplen, las que no tienen prueba y las opiniones en contra. */
export const pendientesDe = (lista: Comprobacion[] | undefined): Comprobacion[] => (lista ?? []).filter((x) => ["no", "sin_prueba", "sin_respuesta", "opinion_no"].includes(x.estado));
/** La escena, comprobada contra su ficha, las reglas del guion y la biblia. Nada de esto toca el documento. */
export type Verificada = {
  /** Es una verificación guardada (de esa fecha): la escena no había cambiado y no se pidió otra. */
  ya_verificada?: string;
  escena: string; titulo: string; palabras: number; guardado: string | null; resumen: string;
  campos: CampoVerificado[];
  /** Cuántos campos cumplen (lo calcula el programa). `con_lista`: los veredictos salen de la lista de comprobaciones. */
  campos_resumen?: CuentaDeCampos; con_lista?: boolean;
  /** Después de esta verificación el coach retiró algo: los veredictos ya lo cuentan, pero el resumen se escribió antes. */
  acordado_despues?: boolean;
  /** `acordado`: el coach lo retiró hablando con el autor; ya no se señala. */
  reglas: { regla: string; cita: string | null; porque: string; acordado?: boolean }[];
  datos: { en_el_texto: string | null; segun: "biblia" | "guion"; dato: string; porque: string; acordado?: boolean }[];
  indicaciones: { campo: string; falta: string; datos: string; donde: string | null; acordado?: boolean }[];
  mas_indicaciones: number; retiradas: number;
  /** Lo que puede ir al documento como cambio marcado, ya pasado por la regla del autor. */
  cambios?: CambioPropuesto[]; cambios_resumen?: ResumenDeCambios & { sin_sitio: number; vueltos_indicacion: number }; con_frases?: boolean;
  biblia: { nombre: string; recortada: boolean } | null; biblias_dudosas: string[];
  modelo: string | null; coste: number | null; ms: number | null; registro: string | null;
};

export type CambioPropuesto = { id: string; tipo: "cortar" | "corregir" | "dato" | "proponer"; original: string; nuevo: string; palabras_no_tuyas: number; campo: string; porque: string };
export type ResumenDeCambios = { cortar: number; corregir: number; dato: number; proponer: number; total: number; palabras_no_tuyas: number; palabras_finales: number; pct: number; zona: "verde" | "ambar" | "rojo" };
/** La versión nueva del manuscrito, con los cambios de la verificación marcados y las indicaciones como comentarios. */
export type Llevada = { escena: string; version: string; de: string; cambios: number; sin_sitio: number; comentarios: number; sin_comentarios: boolean; resumen: ResumenDeCambios; firmas: string[]; abierto: boolean;
  /** Lo que el autor hizo en Word con esos cambios, si ya se ha leído (al cerrar la escena o al llevar otros). */
  leido?: Leido | null };
/** `de`: el Word del que se leyó, si no fue la versión entregada (el autor decidió en uno guardado con otro nombre). */
export type Leido = { aceptado: number; retocado: number; rechazado: number; suyo: number; no_evaluable: number; de_la_ia: number; de?: string };

/**
 * Una tanda de cambios llevada a Word que no se ha leído y espera algo del autor (encargo 0005): su Word sigue como se
 * entregó, con cambios sin decidir, o ya no está. `buscada`: lo decidido se buscó en otros Word de la carpeta y no se
 * encontró (`ninguno`) o se encontró en varios distintos (`varios`).
 */
export type TandaPorLeer = { escena: string; verificacion: number; version: string; estado: "sin_decidir" | "con_marcas" | "sin_fichero"; marcas?: number;
  buscada?: { motivo: "ninguno" | "varios"; varios?: string[] } };
/** Lo que contesta el motor al pulsar «La decidí en otro documento»: cada tanda, leída (`de`, `resultados`) o no. */
export type TandaLeida = { escena: string; verificacion: number; version: string; estado: string; resultados?: string[]; de?: string; por?: string; marcas?: number };
export type LecturaDeTanda = { tandas: TandaLeida[]; medida_novela?: NovelaEstado["medida"] | null };

/**
 * Cuánto de una escena es del autor según el registro (lo que se llevó a Word, lo que él decidió y lo que hay en el texto).
 * `parcial`: alguna tanda no se puede medir (un registro de antes del 10-oct), y la cifra es «al menos». `sin_registro`:
 * no hay con qué decirlo.
 */
export type Medida = {
  registro: "completo" | "parcial" | "sin_cambios" | "sin_registro";
  palabras?: number; tuyas?: number; corregidas?: number; de_la_ia?: number; pct_propio?: number; pct_ia?: number;
  rondas?: number; leidas?: number; de_la_ia_max?: number; pct_propio_min?: number;
  /** De las de la IA, las que están en el texto sin que conste que el autor las aceptara (encargo 0009). */
  por_presencia?: number;
  /** Las tandas que no se pueden medir, y por qué (un motor anterior no lo manda: entonces «parcial» es «sin leer»). */
  sin_medir?: { verificacion: number; version: string | null; motivo: string }[];
  /** El semáforo de la IA para esta medida; sin registro no hay color. */
  zona?: Zona | null;
  /** Más del 5 % de las palabras no son del autor (con lo peor que puede ser, si hay tandas sin leer). Lo decide el motor. */
  aviso?: boolean;
};
/** Verde, hasta el 20 % de IA; amarillo, del 20 al 45; rojo, del 45 en adelante (Rais, 10-oct-2026). La regla la aplica el motor. */
export type Zona = "verde" | "ambar" | "rojo";
export const LEYENDA_DEL_SEMAFORO = "Verde: menos de 20 · amarillo: de 20 a 45 · rojo: de 45 en adelante.";
export const NOMBRE_DE_ZONA: Record<Zona, string> = { verde: "zona verde", ambar: "zona amarilla", rojo: "zona roja" };
export type MedidaDeNovela = { con_registro: number; sin_registro: number; palabras: number; de_la_ia: number; de_la_ia_max: number; parciales: number; pct_propio: number | null; pct_propio_min: number | null; zona?: Zona | null; aviso?: boolean;
  /** De las de la IA, las que están en el texto sin que conste que el autor las aceptara (encargo 0009). */
  por_presencia?: number };

/** A partir de qué parte de palabras que no son del autor avisa «tuyo según el registro». La raya la aplica el motor; aquí va para decirla. */
export const AVISO_DE_REGISTRO = 5;
/** Lo que se dice en letra pequeña junto a las cifras de IA (Rais, 10-oct-2026): son nuestras cuentas, no las de otros detectores. */
export const LETRA_PEQUENA_DE_LA_IA = "Son las cuentas de HumanInk: otros detectores usan otros métodos y pueden dar un resultado distinto.";
/**
 * El aviso de «tuyo según el registro», de una escena o de la novela: solo cuando el motor dice que pasa del
 * 5 %. Con tandas de cambios sin leer la cifra es un «como mucho», y se dice así.
 */
export function fraseDelAviso(m: { aviso?: boolean; registro?: string; parciales?: number; pct_propio?: number | null; pct_propio_min?: number | null } | null | undefined): string {
  if (!m?.aviso) return "";
  const parcial = m.registro === "parcial" || (m.parciales ?? 0) > 0;
  const noTuyo = Math.round((100 - ((parcial ? m.pct_propio_min : m.pct_propio) ?? 100)) * 10) / 10;
  return parcial
    ? `Puede pasar del ${AVISO_DE_REGISTRO} %: hasta el ${porCiento(noTuyo)} de las palabras podría no ser tuyo.`
    : `Pasa del ${AVISO_DE_REGISTRO} %: el ${porCiento(noTuyo)} de las palabras no es tuyo.`;
}

/** Una escena cerrada: el texto que el autor ha dado por bueno, de qué fichero salió y cuándo. */
/** Lo que, al cerrar, coincide con lo que escribió un asesor en la conversación. Solo es un aviso. */
export type Coincide = { palabras: number; tramos: { de: string; palabras: number; cita: string }[]; mas: number };
export type CierreDeEscena = { palabras: number; cuando: string; de: string; entero: boolean; medida?: Medida | null; coincide?: Coincide | null };

const NOMBRE_DE_ASESOR: Record<string, string> = { coach: "el coach", editor: "el editor" };
/** El aviso de coincidencias, o `null` si no hay nada que avisar. Dice lo que es: una coincidencia, no un veredicto. */
export function fraseDeCoincidencia(c: Coincide | null | undefined): string | null {
  if (!c || !c.palabras) return null;
  const quienes = [...new Set(c.tramos.map((t) => NOMBRE_DE_ASESOR[t.de] ?? "un asesor"))];
  return `${c.palabras === 1 ? "1 palabra" : `${miles(c.palabras)} palabras`} de esta escena ${c.palabras === 1 ? "aparece" : "aparecen"}, seguidas, en lo que te escribió ${quienes.join(" y ")} en la conversación. Puede que te estuviera citando; si las copiaste de ahí, no son tuyas. No cambia tu medida.`;
}
/** `datos`: el título y el autor que irán en la portada de la novela (lo último que escribió el autor, o lo que se sabe). */
export type NovelaEstado = { cerradas: Record<string, CierreDeEscena>; total: number; hechas: number; palabras: number; datos?: { titulo: string; autor: string };
  medida?: { escenas: Record<string, Medida>; total: MedidaDeNovela } };
export type Cerrada = CierreDeEscena & { escena: string; igual: boolean; hechas: number; total: number; medida_novela?: NovelaEstado["medida"];
  /** Las tandas de la escena que siguen sin leer tras cerrar (un motor anterior no lo manda). */
  pendientes?: TandaPorLeer[] };
export type Construida = { nombre: string; carpeta: string; escenas: number; total: number; palabras: number; faltan: string[]; faltan_mas: number; abierto: boolean;
  titulo?: string; autor?: string; capitulos?: number };

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
/** «10-oct, 03:50» de una fecha ISO, en la hora del autor; "" si no hay fecha. */
export function diaYHora(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${d.getDate()}-${MESES[d.getMonth()]}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Un turno de la conversación con el coach sobre una verificación. */
export type Turno = { de: "autor" | "coach"; texto: string; cuando?: string };
export type Discutida = { escena: string; respuesta: string; retiradas: number; escena_cambiada: boolean; turnos: number; coste: number | null;
  /** Lo que el coach ha retirado en esta respuesta, y la verificación ya recalculada con ello. */
  retirados?: { codigo: string; texto: string }[]; verificada?: Verificada };
/** Lo último de una escena, tal como está guardado junto al libro: se enseña al abrir sin volver a pagar. */
export type LoUltimo = { verificacion: Verificada | null; hilo: Turno[]; llevada: Omit<Llevada, "abierto"> | null; escena_cambiada: boolean;
  /** Este documento ES la versión que lleva los cambios de esa verificación. */
  en_su_version?: boolean;
  /** Las tandas de la escena que esperan algo del autor; se mira sin leer nada (un motor anterior no lo manda). */
  pendientes?: TandaPorLeer[] };

const tantas = (n: number, una: string, varias: string) => `${miles(n)} ${n === 1 ? una : varias}`;

/** La medida de una escena, dicha en una frase. Nunca un porcentaje que el registro no pueda sostener. */
export function fraseDeMedida(m: Medida | null | undefined): string {
  if (!m) return "";
  if (m.registro === "sin_registro") return "Sin registro: esta escena no pasó por la verificación, así que no se puede decir cuánto es tuyo.";
  if (m.registro === "sin_cambios") return "Tuyo según el registro: 100 %. Se verificó y no se llevó ningún cambio a Word.";
  const ia = m.de_la_ia ?? 0;
  if (m.registro === "parcial") {
    const tope = `como mucho, ${tantas(m.de_la_ia_max ?? ia, "palabra", "palabras")} de la IA.`;
    // Desde el encargo 0009 solo es «al menos» por una tanda de un registro de antes del 10-oct, que no se puede medir. Se
    // dice eso y ningún botón: no lo hay que lo arregle.
    if (m.sin_medir?.length) {
      const n = m.sin_medir.length;
      return `Tuyo según el registro: al menos ${porCiento(m.pct_propio_min ?? 0)}. ${n === 1 ? "Una tanda de cambios de esta escena es de una versión de HumanInk anterior al 10 de octubre y no se puede medir"
        : `${n} tandas de cambios de esta escena son de una versión de HumanInk anterior al 10 de octubre y no se pueden medir`}; ${tope}`;
    }
    const sinLeer = (m.rondas ?? 0) - (m.leidas ?? 0);
    return `Tuyo según el registro: al menos ${porCiento(m.pct_propio_min ?? 0)}. ${sinLeer === 1 ? "Hay una tanda de cambios que no se pudo leer" : `Hay ${sinLeer} tandas de cambios que no se pudieron leer`}; ${tope}`;
  }
  // Lo de la IA que está en el texto sin que conste que lo aceptaras (lo pegaste después de decidir, o la tanda sigue sin
  // leer): cuenta igual, y se dice cuánto es (encargo 0009).
  const p = Math.min(m.por_presencia ?? 0, ia);
  const sinConstar = p <= 0 ? "" : p >= ia ? (ia === 1 ? " Está en el texto sin que conste que la aceptaras." : " Todas están en el texto sin que conste que las aceptaras.")
    : p === 1 ? " 1 de ellas está en el texto sin que conste que la aceptaras." : ` ${miles(p)} de ellas están en el texto sin que conste que las aceptaras.`;
  return ia === 0 ? "Tuyo según el registro: 100 %. De lo que se llevó a Word no queda ninguna palabra que no sea tuya."
    // Sin el total de palabras: va en la línea del cierre, contado como lo cuenta Word («4:10.» es una palabra),
    // y aquí se cuenta letra a letra. Dos totales distintos, uno debajo de otro, parecerían un error.
    : `Tuyo según el registro: ${porCiento(m.pct_propio ?? 0)}. ${ia === 1 ? "Queda" : "Quedan"} ${tantas(ia, "palabra", "palabras")} de la IA.${sinConstar}`;
}

/** La medida de la novela: solo las escenas cerradas que tienen registro, y cuántas son. */
export function fraseDeNovela(t: MedidaDeNovela | null | undefined): string {
  if (!t || t.con_registro + t.sin_registro === 0) return "";
  const cerradas = t.con_registro + t.sin_registro;
  if (!t.con_registro) return `${cerradas === 1 ? "La escena cerrada no tiene" : `Ninguna de las ${tantas(cerradas, "escena cerrada", "escenas cerradas")} tiene`} registro: no se puede decir cuánto es tuyo.`;
  const cifra = t.parciales ? `al menos ${porCiento(t.pct_propio_min ?? 0)}` : porCiento(t.pct_propio ?? 0);
  return `Tuyo según el registro: ${cifra}, en ${t.con_registro} de ${tantas(cerradas, "escena cerrada", "escenas cerradas")}${t.sin_registro ? ` (${tantas(t.sin_registro, "no tiene", "no tienen")} registro)` : ""}.`;
}

/** «2 aceptados · 1 rechazado»: lo que el autor hizo en Word con lo que se le llevó. */
export function fraseDeLoLeido(l: Leido | null | undefined): string {
  if (!l) return "";
  const dice = (n: number, uno: string, varios: string) => (n > 0 ? `${n} ${n === 1 ? uno : varios}` : "");
  return [dice(l.aceptado, "aceptado", "aceptados"), dice(l.retocado, "retocado", "retocados"), dice(l.rechazado, "rechazado", "rechazados"),
    dice(l.suyo, "cambiado por texto tuyo", "cambiados por texto tuyo"), dice(l.no_evaluable, "que no se puede saber", "que no se pueden saber")].filter(Boolean).join(" · ");
}

// ── Una tanda decidida y guardada con otro nombre (encargo 0005) ──────────────────────────────────────────────
const POR_LEER = ["sin_decidir", "con_marcas", "sin_fichero"];
/** Las tandas que hay que decirle al autor: las que esperan algo de él y NO están en el documento abierto (esa ya la ve). */
export const tandasQueDecir = (lista: TandaPorLeer[] | TandaLeida[] | null | undefined, abierto: string): TandaPorLeer[] =>
  ((lista ?? []) as TandaPorLeer[]).filter((t) => POR_LEER.includes(t.estado) && t.version !== abierto);

/** «Tienes una tanda de cambios sin decidir en «…v02.docx».», con lo que le queda; o que su Word ya no está. */
export function fraseDeTandaSinLeer(t: TandaPorLeer | null | undefined): string {
  if (!t) return "";
  if (t.estado === "sin_fichero") return `La tanda de cambios de «${t.version}» no se puede leer: ese documento ya no está en la carpeta.`;
  const quedan = t.estado === "con_marcas" && t.marcas
    ? ` ${t.marcas === 1 ? "Le queda 1 cambio" : `Le quedan ${miles(t.marcas)} cambios`} sin aceptar ni rechazar.` : "";
  return `Tienes una tanda de cambios sin decidir en «${t.version}».${quedan}`;
}

/** Lo que se buscó en la carpeta, solo si sirve de algo decirlo: la encontró decidida en varios Word y no sabe cuál vale. */
export function fraseDeLaBusqueda(b: TandaPorLeer["buscada"] | null | undefined): string {
  if (b?.motivo !== "varios" || !b.varios?.length) return "";
  return `La he buscado en la carpeta: está decidida en más de un documento (${b.varios.map((n) => `«${n}»`).join(", ")}) y no sé cuál vale.`;
}

export const ROTULO_LA_DECIDI = "La decidí en otro documento";
export const AYUDA_DE_LA_TANDA = "Si la decidiste en este documento, pulsa el botón y la leo de aquí. Si fue en otro, ábrelo y púlsalo allí.";

const DECISIONES = ["aceptado", "retocado", "rechazado", "suyo", "no_evaluable"] as const;
/** Lo leído de una tanda, contado como lo cuenta `loLeido` en el motor (sin las palabras de la IA, que aquí no llegan). */
export function leidoDe(t: TandaLeida): Leido {
  const cuenta: Leido = { aceptado: 0, retocado: 0, rechazado: 0, suyo: 0, no_evaluable: 0, de_la_ia: 0, ...(t.de ? { de: t.de } : {}) };
  for (const d of t.resultados ?? []) { const k = DECISIONES.find((c) => c === d); if (k) cuenta[k] += 1; }
  return cuenta;
}
/** «Leída de «…v02 bueno.docx»: 2 aceptados · 1 rechazado.»: lo que se leyó al pulsar el botón. */
export function fraseDeLeidaEnOtro(t: TandaLeida): string {
  const lo = fraseDeLoLeido(leidoDe(t));
  return `Leída${t.de ? ` de «${t.de}»` : ""}${lo ? `: ${lo}` : ""}.`;
}

/** «1 corte · 2 correcciones · 1 frase propuesta»: lo que hay, con su plural, sin lo que está a cero. */
export function cuentaDeCambios(r: Pick<ResumenDeCambios, "cortar" | "corregir" | "dato" | "proponer">): string {
  const dice = (n: number, uno: string, varios: string) => (n > 0 ? `${n} ${n === 1 ? uno : varios}` : "");
  return [dice(r.cortar, "corte", "cortes"), dice(r.corregir, "corrección", "correcciones"), dice(r.dato, "dato", "datos"),
    dice(r.proponer, "frase propuesta", "frases propuestas")].filter(Boolean).join(" · ");
}

/** «1,9 %», con coma y un decimal como mucho. */
export const porCiento = (n: number): string => `${String(Math.round(n * 10) / 10).replace(".", ",")} %`;

/** «00:11» de una fecha ISO, en la hora del autor; "" si no hay fecha. */
export function horaDe(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** «0,12 $»; por debajo de un céntimo, «menos de 0,01 $». Sin cifra, "". */
/** Un trabajo del motor en segundo plano, como lo cuenta `GET /guion/propuesta`. */
export type TrabajoDelMotor = {
  id: string; estado: "en_curso" | "hecho" | "fallido" | "interrumpido"; error?: string | null;
  progreso?: { hecho: number; total: number; fase?: string } | null;
  pasos?: { titulo: string; estado: "pendiente" | "en_curso" | "hecho" | "fallido" | "interrumpido" }[] | null;
  resultado?: GuionPropuesto | null;
};
/** Lo que deja el guion propuesto desde la biblia (21-oct-2026). */
export type GuionPropuesto = {
  nombre: string; titulo: string; capitulos: number; escenas: number; desarrollados: number[]; sin_desarrollar: number;
  notas: string[]; corte: { capitulo: number; motivo: string } | null; coste: number;
};
/** `GET /guion/propuesta`: qué documentos usaría, cuánto costaría y cómo va el último. */
export type EstadoDeLaPropuesta = {
  hay?: boolean; codigo?: string; error?: string; modelo?: string;
  fuentes?: { tipo: "biblia" | "escaleta" | "estilo"; nombre: string; palabras: number; recortado: boolean }[];
  estimacion?: { capitulos: number; uno: number | null; todos: number | null; minutos?: { uno: number; todos: number } };
  trabajo: TrabajoDelMotor | null;
};

export function dolares(coste: number | null | undefined): string {
  if (typeof coste !== "number" || !Number.isFinite(coste) || coste < 0) return "";
  return coste > 0 && coste < 0.01 ? "menos de 0,01 $" : `${coste.toFixed(2).replace(".", ",")} $`;
}

/** El sitio que el esqueleto deja para escribir la escena. No es texto del autor: no cuenta. */
export const MARCA_INDICACION = "[ESCRIBE AQUÍ:";

/**
 * De lo que Word dice del documento abierto a su ruta en el disco.
 *
 * Word da una ruta («/Users/…/libro.docx», «C:\…»), un `file://…` o, si el documento vive en
 * OneDrive o SharePoint, un `https://…`. Ese último no tiene carpeta en este ordenador: null.
 */
export function rutaLocal(url: string): string | null {
  const u = String(url ?? "").trim();
  if (!u || /^https?:\/\//i.test(u)) return null;
  if (!/^file:\/\//i.test(u)) return u;
  let resto = u.replace(/^file:\/\/(localhost)?/i, "");
  try { resto = decodeURIComponent(resto); } catch { /* un % suelto en el nombre: se deja como viene */ }
  return /^\/[A-Za-z]:[\\/]/.test(resto) ? resto.slice(1) : resto;      // «/C:/…» → «C:/…»
}

/** El final que añade HumanInk al nombre de una versión, el de la que está abierta: « - corrección 2 (estilo).docx». */
const COLA_DE_VERSION = / - (correcci[oó]n|reescritura) \d+ \([^()]*\)\.docx$/i;

/**
 * Con qué pestaña arranca el panel de un documento que es una versión de HumanInk (Rais, 24-oct-2026: pasó Estilo
 * en Corrección, Word abrió la versión nueva y su panel salió en Escritura). La misma regla que el motor
 * (`cadena.COLA`, `esEslabon`), sobre el nombre con las tildes compuestas (NFC), y manda la última cola: «… -
 * reescritura 1 (fase 2) - corrección 2 (estilo).docx» es una corrección. Sin guardar o en la nube: null, como hoy.
 */
export function pestanaDeLaVersion(url: string): "correccion" | "reescritura" | null {
  const ruta = rutaLocal(url);
  if (!ruta) return null;
  const m = COLA_DE_VERSION.exec(nombreDe(ruta).normalize("NFC"));
  if (!m) return null;
  return m[1].toLowerCase().startsWith("correcc") ? "correccion" : "reescritura";
}

/** El nombre del fichero de una ruta, con la barra que traiga. */
/**
 * Los documentos de partida (la biblia, el guion, la escaleta, la guía de estilo) no son el manuscrito. La misma regla
 * que el motor (`guion.esDePartida`): por el nombre, y un manuscrito con versión nunca lo es (21-oct).
 */
// Por el nombre ENTERO: la palabra delante o al final, antes de su número (equipo rojo, 25-oct: «La voz dormida» es una novela).
const PALABRA_DE_PARTIDA = "biblia|gui[oó]n|escaleta|estilo|voz|sinopsis|estructura|outline|style";
export const PARTIDA_DELANTE = new RegExp(`^(?:(?:la|el|mi) (?=biblia|escaleta|sinopsis|gui[oó]n del escritor))?(?:${PALABRA_DE_PARTIDA})\\b`, "i");
export const PARTIDA_DETRAS = new RegExp(`\\b(?:${PALABRA_DE_PARTIDA})(?: (?:del escritor|propuesto|definitivo|final|nuevo|nueva|revisado|revisada|corregido|corregida|completo|completa|larga|corta))?$`, "i");
export const nombreComoTexto = (n: string) => String(n ?? "").replace(/\.[^.]+$/, "").replace(/\([^)]*\)/g, " ").replace(/[_\-.\s]+/g, " ").trim()
  .replace(/(?:\s+(?:v|versi[oó]n)?\s*\d+)+$/i, "").trim();
export const esDePartida = (nombre: string) => {
  const n = nombreDe(String(nombre ?? "")).normalize("NFC");
  if (!n || /-m\d{1,2}-v\d{1,3}\.docx$/i.test(n)) return false;
  const t = nombreComoTexto(n);
  return PARTIDA_DELANTE.test(t) || PARTIDA_DETRAS.test(t);
};
/** Lo que se dice cuando el chat iba a escribir en uno de ellos (Rais, 21-oct: el coach escribió encima del título de su biblia). */
export const fraseDeNoAqui = (nombre: string) =>
  `Esto entraría en «${nombre}», que es uno de tus documentos de partida, no el manuscrito. Abre el manuscrito, pon el cursor donde va y vuelve a pulsar.`;

export function nombreDe(ruta: string): string {
  const r = String(ruta ?? "");
  return r.slice(Math.max(r.lastIndexOf("/"), r.lastIndexOf("\\")) + 1);
}

/** La carpeta de una ruta, con la barra que traiga. */
export function carpetaDe(ruta: string): string {
  const i = Math.max(ruta.lastIndexOf("/"), ruta.lastIndexOf("\\"));
  return i > 0 ? ruta.slice(0, i) : "";
}

/**
 * La carpeta con la que trabaja el panel (el chat, los informes, el contexto): la del documento abierto.
 *
 * El panel recordaba la PRIMERA carpeta que vio y ya no la soltaba. Rais pasó su novela de Descargas a su
 * propia carpeta y el chat de Word siguió enseñando la conversación de Descargas, que era de otro libro,
 * mientras la pantalla grande —que sí mira el documento— enseñaba la buena (10-oct-2026).
 *
 *   · documento en una carpeta de este ordenador → esa carpeta, salvo que el autor eligiera otra a mano
 *     PARA los documentos de esa carpeta (`elegidas`);
 *   · documento sin guardar → la última con la que se trabajó;
 *   · documento en la nube (https://…) → como hasta ahora: la recordada y, si no hay, su dirección.
 */
export function carpetaDelProyecto(delDocumento: string, recordada: string, elegidas: Record<string, string> = {}): string {
  const doc = String(delDocumento ?? "").trim(), antes = String(recordada ?? "").trim();
  if (!doc) return antes;
  const local = rutaLocal(doc);
  if (!local) return antes || doc;
  const elegida = elegidas && typeof elegidas === "object" ? elegidas[local] : null;
  return typeof elegida === "string" && elegida.trim() ? elegida.trim() : local;
}

/**
 * El autor ha cargado otra carpeta a mano (Reescritura → Contexto de proyecto): se recuerda para los
 * documentos de la carpeta en la que está. Si vuelve a la del documento, se olvida. No cambia lo que recibe.
 */
export function conLaElegida(elegidas: Record<string, string>, delDocumento: string, elegida: string): Record<string, string> {
  const e = { ...(elegidas && typeof elegidas === "object" ? elegidas : {}) };
  const doc = String(delDocumento ?? "").trim(), x = String(elegida ?? "").trim();
  if (!doc) return e;
  if (x && x !== doc) e[doc] = x; else delete e[doc];
  return e;
}

/** Por qué no se puede abrir el tablero con el documento que hay delante (el tablero es el del libro de ESE documento). */
export function porQueNoHayTablero(url: string): string {
  return String(url ?? "").trim()
    ? "Este documento está en OneDrive o SharePoint. El tablero necesita que el libro esté en una carpeta de este ordenador."
    : "Guarda antes el documento: el tablero es el del libro que tienes abierto.";
}

/** Palabras que el autor lleva escritas en una escena: sin el hueco «[ESCRIBE AQUÍ: …]». */
export function palabrasEscritas(texto: string): number {
  let n = 0;
  for (const parrafo of String(texto ?? "").split(/[\r\n\v]+/)) {
    if (parrafo.trim().startsWith(MARCA_INDICACION)) continue;
    n += (parrafo.match(/\S+/g) ?? []).length;
  }
  return n;
}

/** Cuánto de lo previsto lleva: de 0 a 100 para la barra, o null si la ficha no da cifra. */
export function avance(escritas: number, previstas: number | null): number | null {
  if (!previstas || previstas <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((100 * escritas) / previstas)));
}

/**
 * El texto de la escena tal como lo selecciona el autor en Word, sin el título de la escena si lo ha
 * cogido también («1.2 · B · Tercera persona» no es prosa suya, y contaría como cinco palabras).
 */
export function textoDeEscena(seleccion: string, titulo: string): string {
  const parrafos = String(seleccion ?? "").split(/[\r\n\v]+/);
  const limpio = (s: string) => s.replace(/\s+/g, " ").trim();
  const primero = parrafos.findIndex((p) => limpio(p));
  if (primero >= 0 && titulo && limpio(parrafos[primero]) === limpio(titulo)) parrafos.splice(0, primero + 1);
  return parrafos.join("\n");
}

/** «47350» → «47.350». Los miles con punto, como en el guion. */
export const miles = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
